import type {
  Job,
  Machine,
  Pipeline,
  PipelineItem,
  RunStatus,
  Session,
} from '@orgus/protocol';
import {
  JOB_STATE_LABEL,
  PIPELINE_ITEM_STATE_LABEL,
  PIPELINE_STATE_LABEL,
  RUN_STATUS_LABEL,
  SESSION_STATUS_LABEL,
  machineLiveness,
} from '@orgus/protocol';

/**
 * Every status in the product maps to one of these tones. Components take a Tone,
 * never a raw status, so colour means the same thing on every page.
 */
export type StatusTone = 'working' | 'waiting' | 'idle' | 'ended' | 'passed' | 'failed' | 'queued' | 'offline';
export type Tone = StatusTone | 'neutral' | 'accent';

export const STATUS_TONES: readonly StatusTone[] = [
  'working',
  'waiting',
  'idle',
  'ended',
  'passed',
  'failed',
  'queued',
  'offline',
];

/** Tones that mean something is happening right now: dots pulse, bars grow. */
export function isLiveTone(t: Tone): boolean {
  return t === 'working';
}

/** "Needs you" is the only label for work waiting on a person (content/needs-you-noun). */
export const TONE_LABEL: Record<StatusTone, string> = {
  working: 'Working',
  waiting: 'Needs you',
  idle: 'Idle',
  ended: 'Ended',
  passed: 'Passed',
  failed: 'Failed',
  queued: 'Queued',
  offline: 'Offline',
};

/** Static class names per tone (Tailwind needs literal strings). */
export const TONE_CLASS: Record<Tone, { fg: string; bg: string; soft: string; border: string }> = {
  working: { fg: 'text-working', bg: 'bg-working', soft: 'bg-working-soft', border: 'border-working' },
  waiting: { fg: 'text-waiting', bg: 'bg-waiting', soft: 'bg-waiting-soft', border: 'border-waiting' },
  idle: { fg: 'text-idle', bg: 'bg-idle', soft: 'bg-idle-soft', border: 'border-idle' },
  ended: { fg: 'text-ended', bg: 'bg-ended', soft: 'bg-ended-soft', border: 'border-ended' },
  passed: { fg: 'text-passed', bg: 'bg-passed', soft: 'bg-passed-soft', border: 'border-passed' },
  failed: { fg: 'text-failed', bg: 'bg-failed', soft: 'bg-failed-soft', border: 'border-failed' },
  queued: { fg: 'text-queued', bg: 'bg-queued', soft: 'bg-queued-soft', border: 'border-queued' },
  offline: { fg: 'text-offline', bg: 'bg-offline', soft: 'bg-offline-soft', border: 'border-offline' },
  neutral: { fg: 'text-fg-muted', bg: 'bg-fg-subtle', soft: 'bg-hover', border: 'border-border-strong' },
  accent: { fg: 'text-accent-text', bg: 'bg-accent', soft: 'bg-accent-soft', border: 'border-accent' },
};

/** CSS colour value per tone, for SVG strokes and fills. */
export const TONE_VAR: Record<Tone, string> = {
  working: 'var(--st-working)',
  waiting: 'var(--st-waiting)',
  idle: 'var(--st-idle)',
  ended: 'var(--st-ended)',
  passed: 'var(--st-passed)',
  failed: 'var(--st-failed)',
  queued: 'var(--st-queued)',
  offline: 'var(--st-offline)',
  neutral: 'var(--fg-subtle)',
  accent: 'var(--accent)',
};

export function sessionTone(s: Pick<Session, 'status'>): StatusTone {
  return s.status;
}
export function sessionLabel(s: Pick<Session, 'status'>): string {
  // The wire label for a waiting session predates the language; every view says "Needs you".
  return s.status === 'waiting' ? TONE_LABEL.waiting : SESSION_STATUS_LABEL[s.status];
}

export function runTone(status: RunStatus): StatusTone {
  return status === 'running' ? 'working' : status === 'done' ? 'passed' : status === 'failed' ? 'failed' : 'ended';
}
export function runLabel(status: RunStatus): string {
  return RUN_STATUS_LABEL[status];
}

export function pipelineItemTone(i: Pick<PipelineItem, 'state'>): StatusTone {
  switch (i.state) {
    case 'queued':
      return 'queued';
    case 'active':
      return 'working';
    case 'passed':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'ended';
  }
}
export function pipelineItemLabel(i: Pick<PipelineItem, 'state'>): string {
  return PIPELINE_ITEM_STATE_LABEL[i.state];
}

export function pipelineTone(p: Pick<Pipeline, 'state'>): StatusTone {
  switch (p.state) {
    case 'running':
      return 'working';
    case 'idle':
      return 'idle';
    case 'paused':
      return 'waiting';
    case 'down':
      return 'offline';
    case 'error':
      return 'failed';
  }
}
export function pipelineLabel(p: Pick<Pipeline, 'state'>): string {
  return PIPELINE_STATE_LABEL[p.state];
}

export function jobTone(j: Pick<Job, 'state'>): StatusTone {
  return j.state === 'running' ? 'working' : j.state === 'passed' ? 'passed' : j.state === 'failed' ? 'failed' : 'ended';
}
export function jobLabel(j: Pick<Job, 'state'>): string {
  return JOB_STATE_LABEL[j.state];
}

export function machineTone(m: Pick<Machine, 'online' | 'lastSeen'>, now: number): StatusTone {
  const l = machineLiveness(m, now);
  return l === 'online' ? 'passed' : l === 'stale' ? 'waiting' : 'offline';
}
export function machineLabel(m: Pick<Machine, 'online' | 'lastSeen'>, now: number): string {
  const l = machineLiveness(m, now);
  return l === 'online' ? 'Online' : l === 'stale' ? 'Not reporting' : 'Offline';
}
